import axios, { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/nextjs';
import _ from 'lodash';
import { LoginPageType } from '@asset/enum/enum';
import pMemoize from 'p-memoize';
import ExpiryMap from 'expiry-map';
import {
  redirectToLoginPage,
  redirectToUrl,
} from '@asset/function/urlFunctions';

declare module 'axios' {
  export interface AxiosRequestConfig {
    handleError?: HandleError;
    _retry?: boolean;
  }
}

type ErrorResponse = {
  message: string;
  code: string;
  data?: any;
};

// When the errror is successfully handled, return true, else return false.
export type HandleError = (status: number, data: ErrorResponse) => boolean;

const tokenRefreshAPI = axios.create({
  baseURL: `${process.env.NEXT_PUBLIC_ENV_HOST}`,
  withCredentials: true,
});

const refreshTokenCache = new ExpiryMap(2000);
const refreshToken = pMemoize(
  async () => {
    return await tokenRefreshAPI.post('/api/users/token/refresh/');
  },
  { cache: refreshTokenCache },
);

const authAPI = axios.create({
  baseURL: `${process.env.NEXT_PUBLIC_ENV_HOST}`,
  withCredentials: true,
});

const handleResponseInternal = (response) => {
  return response;
};

const handleErrorInternal = async (error: AxiosError<ErrorResponse>) => {
  const {
    method,
    url,
    params,
    data: requestData,
    headers,
    handleError,
    _retry,
  } = error.config; // axios의 error객체

  if (_.isNil(error.response)) return Promise.reject(error);
  const { data: responseData, status } = error.response;

  if (status === 401) {
    // 카카오 인앱 브라우저로 접속 시 401에러가 발생해도 로그인 페이지 리다이렉트 처리하지 않고 무시
    const userAgent = navigator.userAgent.toLowerCase();
    if (/kakaotalk/i.test(userAgent)) {
      return Promise.reject(error);
    }
  }

  if (status === 401 && !_retry) {
    if (
      url !== '/api/users/token/refresh/' &&
      url !== '/api/users/token/blacklist/' &&
      (responseData.message === 'ACCESS_TOKEN_EXPIRED' ||
        responseData.message === 'ACCESS_TOKEN_NOT_FOUND')
    ) {
      const originalRequest = error.config;
      originalRequest._retry = true;
      try {
        const { data } = await refreshToken();
        const { access } = data;

        originalRequest.headers['Authorization'] = `Bearer ${access}`;
        authAPI.defaults.headers.common['Authorization'] = `Bearer ${access}`;

        return authAPI(originalRequest);
      } catch (error) {
        error.config = {
          ...error.config,
          handleError,
        };
        await handleErrorInternal(error);
        return Promise.reject(error);
      }
    } else if (responseData.code === 'LOGIN_FAILED') {
      toast.error('이메일 혹은 비밀번호를 확인해주세요.');
      return Promise.reject(error);
    } else {
      localStorage.removeItem('persistAtom');
    }
  }

  if (!_.isNil(handleError) && handleError(status, responseData)) {
    return Promise.reject(error);
  }
  if (status === 401) {
    redirectToLoginPage(LoginPageType.NORMAL, true);
    return Promise.reject(error);
  }
  if (status === 403) {
    redirectToUrl({
      pathname: '/_error',
    });
    if (responseData.code === 'TEST_IN_PROGRESS') {
      redirectToUrl({
        pathname: '/error/unsubmitted-test',
        query: {
          testAssignmentInProgress: JSON.stringify(
            responseData.data.testAssignmentInProgress,
          ),
        },
      });
    }
    return Promise.reject(error);
  }
  if (status === 409) {
    return Promise.reject(error);
  }
  if (Math.floor(status / 100) === 5) {
    toast.error('요청을 처리할 수 없습니다. 관리자에게 문의하세요.');
  }
  // DEPRECATED: 글로벌 스코프에 컨텍스트 설정하면 다음 에러 기록에 남아있을 수 있으므로
  // apiUtils.ts/handleError의 Sentry scope내에서 로깅. 하위호환을 위해 남겨둠.
  Sentry.setContext('API Request Detail', {
    method,
    url,
    params,
    requestData,
    headers,
  });
  Sentry.setContext('API Response Detail', {
    status,
    responseData,
  });

  return Promise.reject(error);
};

authAPI.interceptors.response.use(handleResponseInternal, handleErrorInternal);

export { authAPI };
